import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import CampoEditavel from "./CampoEditavel";
import palette from "../../common/styles/palette";

/**
 * Lista de linhas de texto — o Roteiro do paciente e os Materiais.
 *
 * A LISTA VAI INTEIRA A CADA ALTERAÇÃO. Não é desleixo: a lista branca do
 * servidor (`DraftPatch::SCALAR_PATHS`) tipa estes caminhos como
 * `list<string>` e não tem operação de "acrescentar item". Criar uma op nova
 * aqui só para economizar bytes abriria um segundo caminho de escrita — o que
 * o módulo inteiro existe para evitar. Substituir o vetor completo é uma
 * operação idempotente que passa pela mesma validação de sempre.
 *
 * A linha nova mora em estado LOCAL até ter texto. Um `set` com string vazia
 * dentro do vetor gravaria lixo no Rascunho e faria a Lacuna do campo parecer
 * respondida sem que nada tivesse sido dito.
 *
 * Apagar o texto de uma linha existente REMOVE a linha. É o gesto que o
 * professor já tenta fazer, e manter uma linha vazia no vetor tem o mesmo
 * defeito de gravar lixo.
 */
interface Props {
  itens: string[];
  onComitar: (novos: string[]) => void;
  disabled?: boolean;
  /** Texto do campo de linha nova. */
  placeholder?: string;
  /** O que dizer quando ainda não há nenhuma linha. */
  vazio?: string;
}

export default function ListaEditavel({
  itens,
  onComitar,
  disabled = false,
  placeholder,
  vazio,
}: Props) {
  const { t } = useTranslation();
  const [novo, setNovo] = useState("");

  const trocar = (indice: number, texto: string) => {
    const limpo = texto.trim();
    const novos =
      limpo === ""
        ? itens.filter((_, i) => i !== indice)
        : itens.map((v, i) => (i === indice ? limpo : v));
    onComitar(novos);
  };

  const remover = (indice: number) =>
    onComitar(itens.filter((_, i) => i !== indice));

  const acrescentar = () => {
    const limpo = novo.trim();
    if (limpo === "") return;
    setNovo("");
    onComitar([...itens, limpo]);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      {itens.length === 0 && vazio && (
        <Typography
          variant="caption"
          sx={{ color: palette.textMuted, mb: 0.5 }}
        >
          {vazio}
        </Typography>
      )}

      {itens.map((linha, indice) => (
        <Box
          // O índice É a identidade aqui: a lista é um vetor de texto sem `ref`,
          // e o servidor devolve outro vetor a cada turno. Qualquer chave
          // derivada do conteúdo colidiria em duas linhas iguais.
          key={indice}
          sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}
        >
          <Typography
            variant="caption"
            sx={{ color: palette.textMuted, mt: 1, minWidth: 16 }}
          >
            {indice + 1}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <CampoEditavel
              valor={linha}
              multiline
              fullWidth
              disabled={disabled}
              onComitar={(v) => trocar(indice, v)}
            />
          </Box>
          <Button
            size="small"
            color="error"
            disabled={disabled}
            onClick={() => remover(indice)}
            sx={{ minWidth: 0, px: 0.5, fontSize: 10, textTransform: "none" }}
          >
            {t("interview_remove")}
          </Button>
        </Box>
      ))}

      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, mt: 0.5 }}>
        <TextField
          size="small"
          variant="standard"
          fullWidth
          multiline
          value={novo}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => setNovo(e.target.value)}
          onKeyDown={(e) => {
            // Enter comete; Shift+Enter continua quebrando linha, porque uma
            // fala do paciente pode ter mais de uma frase.
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              acrescentar();
            }
          }}
        />
        <Button
          size="small"
          disabled={disabled || novo.trim() === ""}
          onClick={acrescentar}
        >
          {t("interview_list_add")}
        </Button>
      </Box>
    </Box>
  );
}
